<template>
    <div>
        <div id="autocomplete"></div>
        <div id="autocomplete-panel"></div>
        <button type="button" class="aa-DetachedCancelButton" aria-label="Close Search" @click="closeAutocompletePanel">
            Close Search
        </button>
    </div>
    
</template>
  
<script lang="jsx">
    import { liteClient as algoliasearch } from 'algoliasearch/lite';
    import { h, Fragment, ref, render, onMounted, onBeforeUnmount } from 'vue';
    import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
    import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
    import { createRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
    import { debounce } from 'lodash';
    import SearchProductImage from '@/components/SearchProduct/SearchProductImage.vue';
    import { useSessionStore } from "@/store/storeSession.js";
    import { sanitizeForUrl } from '@/utils/algoliaSearch.js';
    import { buildPdpUrl } from '@/utils/algoliaSearch.js';
    import { initAlgoliaInsights, sendAlgoliaEvent } from '@/utils/algoliaInsights.js';

    import '@/css/autocompleteThemeClassic.css';

    let showMinMessage = false;
    let lastQuery = '';
    let canSaveRecentSearch = true;
    let productResultCounts = {};
    let specialsStates = {};
    let specialsProductsCache = {
        storeNum: '',
        hits: [],
    };
    let hasSpecialsProductsCache = false;
    let autocompleteInstance = null;
    let allowMobileClose = false;
    let lastAutocompleteOpenState = null;

    const RECENT_SEARCHES_KEY = 'recentSearches';
    const MAX_RECENT_SEARCHES = 5;
    const LEFT_COLUMN_TOTAL_MAX = 10;
    const LEFT_COLUMN_SUGGESTIONS_ONLY_MAX = 8;
    const LEFT_COLUMN_SUGGESTIONS_WITH_AUX_MAX = 5;
    const LEFT_COLUMN_AUX_MAX = 5;
    const MAX_DEPARTMENT_SUGGESTIONS = 5;
    const DEPARTMENT_RELEVANCE_HITS = 20;
    const MAX_ITEM_RESULTS_DESKTOP = 4;
    const MAX_ITEM_RESULTS_MOBILE = 8;
    const SPECIALS_POOL_SIZE = 12;
    const MIN_QUERY_LENGTH = 3;
    const PRODUCTS_COUNT_CLASS_PREFIX = 'item-count-';

    const getBaseSuggestionProductLimit = (query = '') => {
        return LEFT_COLUMN_TOTAL_MAX;
    };

    const getMaxItemResults = () => {
        return window.matchMedia('(max-width: 768px)').matches ? MAX_ITEM_RESULTS_MOBILE : MAX_ITEM_RESULTS_DESKTOP;
    };

    const getMaxSuggestionResults = () => {
        return window.matchMedia('(max-width: 768px)').matches ? LEFT_COLUMN_SUGGESTIONS_WITH_AUX_MAX : LEFT_COLUMN_SUGGESTIONS_ONLY_MAX;
    };

    const getRecentSearchesCount = () => {
        try {
            const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed.length : 0;
        } catch (err) {
            return 0;
        }
    };

    const createRecentSearchesStorage = (limit = MAX_RECENT_SEARCHES, shouldSaveItem = () => true) => {
        const read = () => {
            try {
                const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
                const parsed = raw ? JSON.parse(raw) : [];
                return Array.isArray(parsed) ? parsed : [];
            } catch (err) {
                return [];
            }
        };

        const write = (items) => {
            try {
                window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(items.slice(0, limit)));
            } catch (err) {
                // localStorage can fail in private mode; ignore and keep autocomplete functional
            }
        };

        return {
            onAdd(item) {
                const searchLabel = item && typeof item.label === 'string' ? item.label.trim() : '';

                if (searchLabel.length < MIN_QUERY_LENGTH) {
                    return;
                }

                if (!shouldSaveItem(item)) {
                    return;
                }

                const items = read().filter((x) => x.id !== item.id);
                write([item, ...items]);
            },
            onRemove(id) {
                write(read().filter((x) => x.id !== id));
            },
            getAll(query = '') {
                const items = read();
                return items.slice(0, limit);
            },
        };
    };

    export default {
        name: 'AutoCompleteSearch',
        setup() {
            const sessionStore = useSessionStore();
            initAlgoliaInsights(sessionStore);
            let keepOpenOnFocusTransition = false;

            const pushDataLayerEvent = (eventName, payload = {}) => {
                if (typeof window === 'undefined') {
                    return;
                }

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: eventName,
                    ...payload,
                });
            };

            const trackAutocompleteClick = ({ item, eventName, fallbackEventName, fallbackLabel }) => {
                pushDataLayerEvent(fallbackEventName, {
                    label: fallbackLabel,
                    objectID: item?.objectID || '',
                });

                if (!sessionStore.trackAlgolia || !item || !item.objectID) {
                    return;
                }

                const hasAfterSearchData = Boolean(item.__queryID) && Number.isFinite(Number(item.__position));
                const eventType = hasAfterSearchData ? 'clickedObjectIDsAfterSearch' : 'clickedObjectIDs';

                const eventPayload = {
                    eventType: 'click',
                    eventName,
                    index: sessionStore.index,
                    objectIDs: [item.objectID],
                    userToken: sessionStore.userToken || '',
                    authenticatedUserToken: sessionStore.authUserToken || ''
                };

                if (hasAfterSearchData) {
                    eventPayload.queryID = item.__queryID;
                    eventPayload.positions = [Number(item.__position)];
                }

                sendAlgoliaEvent(sessionStore, eventType, eventPayload);
            };

            const trackAutocompleteProductClick = (item) => {
                trackAutocompleteClick({
                    item,
                    eventName: 'Autocomplete Result Click',
                    fallbackEventName: 'autocomplete_product_click',
                    fallbackLabel: item?.usfitem || item?.objectID || '',
                });
            };

            const isFocusWithinAutocomplete = (element) => {
                if (!element) {
                    return false;
                }

                const autocompleteRoot = document.querySelector('#autocomplete');
                const panelContainer = document.querySelector('#autocomplete-panel');

                return Boolean(
                    (autocompleteRoot && autocompleteRoot.contains(element))
                    || (panelContainer && panelContainer.contains(element))
                );
            };

            const getFocusablePanelElements = () => {
                const panelContainer = document.querySelector('#autocomplete-panel');

                if (!panelContainer) {
                    return [];
                }

                return Array.from(
                    panelContainer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
                ).filter((element) => {
                    return element.offsetParent !== null;
                });
            };

            const handleAutocompleteKeydown = (event) => {
                if (event.key !== 'Tab') {
                    return;
                }

                const activeElement = document.activeElement;
                keepOpenOnFocusTransition = isFocusWithinAutocomplete(activeElement);

                window.setTimeout(() => {
                    keepOpenOnFocusTransition = false;
                }, 0);

                if (event.shiftKey) {
                    return;
                }

                const isSearchInputFocused = Boolean(activeElement && activeElement.classList?.contains('aa-Input'));

                if (!isSearchInputFocused) {
                    return;
                }

                const focusablePanelElements = getFocusablePanelElements();

                if (!focusablePanelElements.length) {
                    return;
                }

                event.preventDefault();
                focusablePanelElements[0].focus();

                if (window.autocompleteInstance && typeof window.autocompleteInstance.setIsOpen === 'function') {
                    window.autocompleteInstance.setIsOpen(true);
                }
            };

            const handleAutocompleteFocusIn = (event) => {
                const focusedElement = event && event.target ? event.target : document.activeElement;

                if (!isFocusWithinAutocomplete(focusedElement)) {
                    return;
                }

                if (window.autocompleteInstance && typeof window.autocompleteInstance.setIsOpen === 'function') {
                    window.autocompleteInstance.setIsOpen(true);
                }
            };

            const closeAutocompletePanel = () => {
                allowMobileClose = true;

                if (window.autocompleteInstance && typeof window.autocompleteInstance.setIsOpen === 'function') {
                    window.autocompleteInstance.setIsOpen(false);
                }

                const container = document.querySelector('#autocomplete')?.closest('.algolia-mega-menu-container');
                if (container) {
                    container.classList.remove('is-open');
                }

                const panelContainer = document.querySelector('#autocomplete-panel');
                if (panelContainer) {
                    panelContainer.classList.remove('is-open');
                }

                const autocompleteRoot = document.querySelector('#autocomplete');
                if (autocompleteRoot) {
                    autocompleteRoot.classList.remove('is-open');
                }

                document.body.classList.remove('search-open-mobile');
                pushDataLayerEvent('autocomplete_close', {
                    source: 'close_button',
                });
            };

            const attachCloseButtonByViewport = () => {
                const panel = document.querySelector('#autocomplete-panel .aa-Panel');
                const panelContainer = document.querySelector('#autocomplete-panel');
                const autocompleteRoot = document.querySelector('#autocomplete');
                const closeButton = document.querySelector('.aa-DetachedCancelButton');
                const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

                if (!closeButton || !panelContainer || !autocompleteRoot) {
                    return;
                }

                if (isMobileViewport) {
                    if (!autocompleteRoot.contains(closeButton)) {
                        autocompleteRoot.appendChild(closeButton);
                    }

                    return;
                }

                if (panel) {
                    if (!panel.contains(closeButton)) {
                        panel.appendChild(closeButton);
                    }

                    return;
                }

                if (!panelContainer.contains(closeButton)) {
                    panelContainer.appendChild(closeButton);
                }
            };

            const algoliaClient = algoliasearch(
                sessionStore.appId,
                sessionStore.apiKey
            );

            const getDeptName = (dept) => {
                let deptName = sessionStore.departments[dept] ? sessionStore.departments[dept].deptName : '';

                if (!deptName) {
                    let decodedDept = dept;

                    try {
                        decodedDept = decodeURIComponent(dept);
                    } catch (err) {
                        decodedDept = dept;
                    }

                    deptName = sessionStore.departments[decodedDept] ? sessionStore.departments[decodedDept].deptName : '';
                }

                return deptName || dept;
            };

            const getUnitPrice = (item) => {
                if (!item) {
                    return '';
                }

                if(item.scaleItem[sessionStore.storeNum] == 1) {
                    return `${item.unitSellPrice[sessionStore.storeNum]}/lb`;
                }

                return item.unitSellPrice[sessionStore.storeNum];
            };

            const getCasePrice = (item) => {
                if (!item) {
                    return '';
                }

                if(item.scaleItem[sessionStore.storeNum] == 1) {
                    return `${item.unitSellPrice[sessionStore.storeNum]}/lb`;
                }

                return item.caseSellPrice[sessionStore.storeNum];
            };

            const displayCaseSale = (item) => {
                if (!item) {
                    return '';
                }

                if(item.scaleItem[sessionStore.storeNum] == 1) {
                    return item.showUnitSale?.[sessionStore.storeNum];
                }

                return item.showCaseSale?.[sessionStore.storeNum];
            };

            const getStoreDescription = (item) => {
                if (!item) {
                    return '';
                }

                if (item.itemDescStore && sessionStore.storeNum in item.itemDescStore) {
                    if (item.itemDescStore[sessionStore.storeNum] === 1 && item.itemDesc1) {
                        return item.itemDesc1;
                    }

                    if (item.itemDescStore[sessionStore.storeNum] === 2 && item.itemDesc2) {
                        return item.itemDesc2;
                    }
                }

                if (typeof item.itemDesc1 === 'string' && item.itemDesc1.trim().length > 0) {
                    return item.itemDesc1;
                }

                if (typeof item.itemDesc2 === 'string' && item.itemDesc2.trim().length > 0) {
                    return item.itemDesc2;
                }

                if (typeof item.itemDesc === 'string' && item.itemDesc.trim().length > 0) {
                    return item.itemDesc;
                }

                return '';
            };

            const escapeRegExp = (value = '') => {
                return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            };

            const renderQueryStyledText = (text = '', query = '') => {
                const safeText = typeof text === 'string' ? text : '';
                const trimmedQuery = typeof query === 'string' ? query.trim() : '';

                if (!safeText.length || !trimmedQuery.length) {
                    return safeText;
                }

                const queryPattern = new RegExp(`(${escapeRegExp(trimmedQuery)})`, 'ig');
                const parts = safeText.split(queryPattern).filter((part) => part.length > 0);

                if (!parts.length) {
                    return safeText;
                }

                return parts.map((part, index) => {
                    if (part.toLowerCase() === trimmedQuery.toLowerCase()) {
                        return (
                            <mark key={`match-${index}`}>
                                {part}
                            </mark>
                        );
                    }

                    return part;
                });
            };

            const getDepartmentSuggestions = async (query) => {
                if (query.trim().length < 1) {
                    return [];
                }

                try {
                    const response = await algoliaClient.search([
                        {
                            indexName: sessionStore.index,
                            query,
                            params: {
                                hitsPerPage: DEPARTMENT_RELEVANCE_HITS,
                                attributesToRetrieve: ['dept', 'department'],
                                filters: `storeNum=${sessionStore.storeNum}`,
                            },
                        },
                    ]);

                    const hits = response && response.results && response.results[0] && response.results[0].hits
                        ? response.results[0].hits
                        : [];

                    if (!hits.length) {
                        return [];
                    }

                    const departmentsByRelevance = [];
                    const seenDepartments = new Set();

                    hits.forEach((hit) => {
                        const dept = hit && hit.dept ? String(hit.dept) : '';

                        if (!dept || seenDepartments.has(dept) || departmentsByRelevance.length >= MAX_DEPARTMENT_SUGGESTIONS) {
                            return;
                        }

                        seenDepartments.add(dept);
                        departmentsByRelevance.push({
                            dept,
                            label: hit.department && String(hit.department).trim().length ? hit.department : getDeptName(dept),
                        });
                    });

                    return departmentsByRelevance;
                } catch (err) {
                    return [];
                }
            };

            const getSpecialsProducts = async () => {
                const storeNum = String(sessionStore.storeNum || sessionStore.userStore?.storeNum || '').trim();
                const maxItemResults = getMaxItemResults();

                if (!storeNum.length) {
                    return [];
                }

                if (hasSpecialsProductsCache && specialsProductsCache.storeNum === storeNum) {
                    return specialsProductsCache.hits.slice(0, maxItemResults);
                }
                
                const response = await algoliaClient.search([
                    {
                        indexName: sessionStore.index,
                        query: '',
                        params: {
                            hitsPerPage: SPECIALS_POOL_SIZE,
                            attributesToSnippet: ['itemDesc1:100', 'itemDesc2:100'],
                            snippetEllipsisText: '…',
                            clickAnalytics: true,
                            filters: `categoryPageID.${storeNum}:"On Sale"`,
                        },
                    }
                ]);

                const result = response && response.results && response.results[0] ? response.results[0] : { hits: [] };
                const hits = Array.isArray(result.hits) ? result.hits : [];

                const shuffledHits = hits.slice();

                for (let i = shuffledHits.length - 1; i > 0; i -= 1) {
                    const randomIndex = Math.floor(Math.random() * (i + 1));
                    const temp = shuffledHits[i];
                    shuffledHits[i] = shuffledHits[randomIndex];
                    shuffledHits[randomIndex] = temp;
                }

                specialsProductsCache = {
                    storeNum,
                    hits: shuffledHits,
                };
                hasSpecialsProductsCache = true;

                return shuffledHits.slice(0, maxItemResults);
            };

            const recentSearchesPlugin = createRecentSearchesPlugin({
                limit: MAX_RECENT_SEARCHES,
                storage: createRecentSearchesStorage(MAX_RECENT_SEARCHES, () => canSaveRecentSearch),
                transformSource({ source, state }) {
                    return {
                        ...source,
                        getItems({ query }) {
                            return source.getItems({ query });
                        },
                        templates: {
                            ...source.templates,
                            header({ state }) {
                                return (
                                    <div className="aa-ItemWrapper aa-ItemWrapper--label">
                                        <div className="aa-ItemContent">
                                            <div className="aa-ItemContentBody">
                                                <div className="aa-ItemContentTitle">
                                                    <span className="result-header">Recent Searches</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                            item({ item, state }) {
                                return (
                                    <div className="aa-ItemWrapper">
                                        <div className="aa-ItemContent">
                                            <div className="aa-ItemContentBody">
                                                <div className="aa-ItemContentTitle">
                                                    <a class="result-recent" href={`/search/fullsearch/${sanitizeForUrl(item.label)}/`}>
                                                        {item.label}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                        },
                    };
                },
            });

            const querySuggestionsPlugin = createQuerySuggestionsPlugin({
                searchClient: algoliaClient,
                indexName: sessionStore.index + "_query_suggestions",
                getSearchParams({ state }) {
                    const currentQuery = state && state.query ? state.query : '';

                    if (currentQuery.trim().length >= 1) {
                        return {
                            hitsPerPage: getBaseSuggestionProductLimit(currentQuery),
                        };
                    }

                    return recentSearchesPlugin.data.getAlgoliaSearchParams({
                        hitsPerPage: getBaseSuggestionProductLimit(currentQuery),
                    });
                },
                transformSource({ source }) {
                    const hasAlternativeSearchResults = (state) => {
                        const collections = state && Array.isArray(state.collections) ? state.collections : [];
                        const currentQuery = state && typeof state.query === 'string' ? state.query.trim() : '';
                        const hideSpecials = currentQuery.length >= 1 && specialsStates[currentQuery] === true;

                        return collections.some((collection) => {
                            if (!collection || !collection.source) {
                                return false;
                            }

                            const sourceId = collection.source.sourceId;
                            const isAlternativeSource = sourceId === 'products' || sourceId === 'departments';

                            if (sourceId === 'products' && hideSpecials) {
                                return false;
                            }

                            return isAlternativeSource && Array.isArray(collection.items) && collection.items.length > 0;
                        });
                    };

                    return {
                        ...source,
                        getItems({ query }) {
                            if (showMinMessage && query.trim().length < MIN_QUERY_LENGTH) {
                                return [];
                            }

                            return source.getItems(arguments[0]);
                        },
                        templates: {
                            ...source.templates,
                            header({ state, items }) {
                                const queryText = state && typeof state.query === 'string' ? state.query.trim() : '';
                                const hasSuggestionItems = Array.isArray(items) && items.length > 0;
                                const isMinMessageState = showMinMessage && queryText.length < MIN_QUERY_LENGTH;

                                if (!isMinMessageState && queryText.length >= 1 && !hasSuggestionItems && hasAlternativeSearchResults(state)) {
                                    return null;
                                }

                                return (
                                    <div className="aa-ItemWrapper aa-ItemWrapper--label">
                                        <div className="aa-ItemContent">
                                            <div className="aa-ItemContentBody">
                                                <div className="aa-ItemContentTitle">
                                                    <span className="result-header">{isMinMessageState || queryText.length >= 1 ? 'Suggestions' : 'Trending'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                            noResults({ state }) {
                                if (showMinMessage && state.query.trim().length < MIN_QUERY_LENGTH) {
                                    return (
                                        <span className="aa-result">Minimum of three characters required</span>
                                    );
                                }

                                if (state.query.trim().length < 1) {
                                    return null;
                                }

                                if (hasAlternativeSearchResults(state)) {
                                    return null;
                                }

                                return (
                                    <span className="aa-result">No Results Found</span>
                                );
                            },
                            item({ item, state, components }) {
                                if (state.query.length >= 1) {
                                    return (
                                        <div className="aa-ItemWrapper">
                                            <div className="aa-ItemContent">
                                                <div className="aa-ItemContentBody">
                                                    <div className="aa-ItemContentTitle">
                                                        <a class="result-trending" href={`/search/fullsearch/${sanitizeForUrl(item.query)}/`}>
                                                            <span class="result-svg">
                                                                <img src="/images/svg/search-light.svg" alt="Search Icon" />
                                                            </span>
                                                            <components.Highlight
                                                                hit={item}
                                                                attribute="query"
                                                            />
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="aa-ItemWrapper">
                                        <div className="aa-ItemContent">
                                            <div className="aa-ItemContentBody">
                                                <div className="aa-ItemContentTitle">
                                                    <a class="result-trending" href={`/search/fullsearch/${sanitizeForUrl(item.query)}/`}>
                                                        <span class="result-svg">
                                                            <img src="/images/svg/search-light.svg" alt="Search Icon" />
                                                        </span>
                                                        {item.query}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                        },
                    };
                },
            });

            const initializeAutocomplete = () => {
                const setContainerOpenState = (isOpen) => {
                    const container = document.querySelector('#autocomplete')?.closest('.algolia-mega-menu-container');
                    const panelContainer = document.querySelector('#autocomplete-panel');
                    const autocompleteRoot = document.querySelector('#autocomplete');
                    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

                    if (panelContainer) {
                        panelContainer.classList.toggle('is-open', Boolean(isOpen));
                    }

                    if (autocompleteRoot) {
                        autocompleteRoot.classList.toggle('is-open', Boolean(isOpen));
                    }

                    document.body.classList.toggle('search-open-mobile', Boolean(isOpen) && isMobileViewport);

                    if (!container) {
                        return;
                    }

                    container.classList.toggle('is-open', Boolean(isOpen));
                };

                const updateProductsListCountClass = (state) => {
                    const collections = state && Array.isArray(state.collections) ? state.collections : [];
                    const productsCollection = collections.find((collection) => collection?.source?.sourceId === 'products');
                    const productsCount = Array.isArray(productsCollection?.items) ? productsCollection.items.length : 0;

                    window.requestAnimationFrame(() => {
                        const productsList = document.querySelector('#appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List');

                        if (!productsList) {
                            return;
                        }

                        Array.from(productsList.classList).forEach((className) => {
                            if (className.startsWith(PRODUCTS_COUNT_CLASS_PREFIX)) {
                                productsList.classList.remove(className);
                            }
                        });

                        productsList.classList.add(`${PRODUCTS_COUNT_CLASS_PREFIX}${productsCount}`);
                    });
                };

                autocompleteInstance = autocomplete({
                    container: '#autocomplete',
                    panelContainer: '#autocomplete-panel',
                    plugins: [querySuggestionsPlugin, recentSearchesPlugin],
                    openOnFocus: true,
                    placeholder: 'Search products',
                    detachedMediaQuery: 'none',
                    getSources({ query }) {
                        const sanitizedQuery = sanitizeForUrl(query);

                        return [
                            {
                                sourceId: 'products',
                                async getItems() {
                                    if (sanitizedQuery.length < 1) {
                                        productResultCounts = {};
                                        try {
                                            const specialsHits = await getSpecialsProducts();
                                            specialsStates = { '': specialsHits.length > 0 };

                                            return specialsHits;
                                        } catch (err) {
                                            specialsStates = { '': false };
                                            return [];
                                        }
                                    }

                                    try {
                                        const maxItemResults = getMaxItemResults();

                                        const response = await algoliaClient.search([
                                            {
                                                indexName: sessionStore.index,
                                                query,
                                                params: {
                                                    hitsPerPage: maxItemResults,
                                                    attributesToSnippet: ['itemDesc1:100', 'itemDesc2:100'],
                                                    snippetEllipsisText: '…',
                                                    clickAnalytics: true,
                                                    filters: `storeNum=${sessionStore.storeNum}`,
                                                },
                                            },
                                        ]);

                                        const result = response && response.results && response.results[0] ? response.results[0] : { hits: [], nbHits: 0 };
                                        const trimmedQuery = query.trim();

                                        productResultCounts[trimmedQuery] = Number(result.nbHits || 0);

                                        if (Array.isArray(result.hits) && result.hits.length > 0) {
                                            specialsStates[trimmedQuery] = false;
                                            return result.hits;
                                        }

                                        specialsStates[trimmedQuery] = false;
                                        return [];
                                    } catch (err) {
                                        specialsStates[query.trim()] = false;
                                        return [];
                                    }
                                },
                                templates: {
                                    header({ state, items }) {
                                        const stateQuery = state.query ? state.query.trim() : '';
                                        const isSpecials = stateQuery in specialsStates ? specialsStates[stateQuery] : false;

                                        if (!items.length) {
                                            return null;
                                        }

                                        return (
                                            <div className="aa-ItemWrapper aa-ItemWrapper--label">
                                                <div className="aa-ItemContent">
                                                    <div className="aa-ItemContentBody">
                                                        <div className="aa-ItemContentTitle">
                                                            <span className="result-header">{isSpecials ? 'Specials' : 'Products'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    },
                                    item({ item, state }) {
                                        const itemDescription = getStoreDescription(item);
                                        const itemText = renderQueryStyledText(itemDescription, state && typeof state.query === 'string' ? state.query : '');
                                        const unitPrice = getUnitPrice(item);
                                        const casePrice = getCasePrice(item);
                                        const dspCaseSale = displayCaseSale(item);

                                        if (!itemDescription) {
                                            return null;
                                        }
                                        const imageItem = {
                                            ...item,
                                            image: typeof item.image === 'string' ? item.image.trim() : '',
                                        };

                                        return (
                                            <div className="aa-ItemWrapper aa-ItemWrapper--imageResult">
                                                <a className="result-item" href={buildPdpUrl(itemDescription, item.usfitem)} onClick={() => trackAutocompleteProductClick(item)}>
                                                    <span className={`result-item__image ${(item.showUnitSale?.[sessionStore.storeNum] == 1 || dspCaseSale == 1) ? 'is-sale' : ''}`}>
                                                        <SearchProductImage item={imageItem} itemDesc={itemDescription} />
                                                    </span>
                                                    <span className="result-item__brand">{item.brand}</span>
                                                    <span className="result-item__text">{itemText}</span>
                                                    <div className="result-item__info">
                                                        <div className="result-item__info-size">{item.itemSize[sessionStore.storeNum]}</div>
                                                        {item.showUnitSale?.[sessionStore.storeNum] == 0 && (
                                                            <div className="result-item__info-price">${unitPrice}</div>
                                                        )}
                                                        {item.showUnitSale?.[sessionStore.storeNum] == 1 && (
                                                            <div className="result-item__info-price is-sale">${unitPrice}</div>
                                                        )}
                                                        {item.hasCase?.[sessionStore.storeNum] == 1 && (
                                                            <>
                                                            <div className="result-item__info-size">{item.pack?.[sessionStore.storeNum]}&nbsp;Units</div>
                                                            {dspCaseSale == 0 && (
                                                                <div className="result-item__info-price">${casePrice}</div>
                                                            )}
                                                            {dspCaseSale == 1 && (
                                                                <div className="result-item__info-price is-sale">${casePrice}</div>
                                                            )}
                                                            </>
                                                        )}
                                                    </div>
                                                </a>
                                            </div>
                                        );
                                    },
                                    noResults({ state }) {
                                        return null;
                                    },
                                    footer({ state }) {
                                        const stateSanitizedQuery = sanitizeForUrl(state.query);
                                        const stateQuery = state.query ? state.query.trim() : '';
                                        const isSpecials = stateQuery in specialsStates ? specialsStates[stateQuery] : false;
                                        const productsCollection = state.collections.find((collection) => collection.source.sourceId === 'products');
                                        const productsCount = productsCollection ? productsCollection.items.length : 0;
                                        const totalProductsCount = stateQuery in productResultCounts ? productResultCounts[stateQuery] : productsCount;

                                        if (isSpecials) {
                                            return (
                                                <div className="aa-SeeAllWrapper aa-SeeAllWrapper--images">
                                                    <a href="/search/temporarypricereductions/" className="see-all">
                                                        See All Specials
                                                    </a>
                                                </div>
                                            );
                                        }

                                        if (stateSanitizedQuery.length < MIN_QUERY_LENGTH || productsCount < 1) {
                                            return null;
                                        }

                                        return (
                                            <div className="aa-SeeAllWrapper aa-SeeAllWrapper--images">
                                                <a href={`/search/fullsearch/${sanitizeForUrl(state.query)}/`} className="see-all">
                                                    See Results in {stateQuery} ({totalProductsCount})
                                                </a>
                                            </div>
                                        );
                                    },
                                },
                            },
                            {
                                sourceId: 'departments',
                                getItems() {
                                    return getDepartmentSuggestions(query);
                                },
                                templates: {
                                    header({ state, items }) {
                                        if (state.query.trim().length < 1 || !items.length) {
                                            return null;
                                        }

                                        return (
                                            <div className="aa-ItemWrapper aa-ItemWrapper--label">
                                                <div className="aa-ItemContent">
                                                    <div className="aa-ItemContentBody">
                                                        <div className="aa-ItemContentTitle">
                                                            <span className="result-header">Departments</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    },
                                    item({ item }) {
                                        return (
                                            <div className="aa-ItemWrapper">
                                                <div className="aa-ItemContent">
                                                    <div className="aa-ItemContentBody">
                                                        <div className="aa-ItemContentTitle">
                                                            <a class="result-department" href={`/search/${encodeURIComponent(item.dept)}/${sanitizeForUrl(query)}/`}>
                                                                {item.label}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    },
                                },
                            },
                        ];
                    },
                    onStateChange({ state, prevState, setIsOpen }) {
                        const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

                        if (!state.isOpen && !allowMobileClose && keepOpenOnFocusTransition) {
                            setIsOpen(true);
                            setContainerOpenState(true);
                            return;
                        }

                        if (lastAutocompleteOpenState !== state.isOpen) {
                            pushDataLayerEvent(state.isOpen ? 'autocomplete_open' : 'autocomplete_close', {
                                source: 'state_change',
                                query: state.query ? state.query.trim() : '',
                            });
                            lastAutocompleteOpenState = state.isOpen;
                        }

                        if (isMobileViewport && prevState && prevState.isOpen && !state.isOpen && !allowMobileClose) {
                            setIsOpen(true);
                            setContainerOpenState(true);
                            return;
                        }

                        if (!state.isOpen && allowMobileClose) {
                            allowMobileClose = false;
                        }

                        if (!state.isOpen) {
                            specialsProductsCache = {
                                storeNum: '',
                                hits: [],
                            };
                            hasSpecialsProductsCache = false;
                        }

                        setContainerOpenState(state.isOpen);
                        window.requestAnimationFrame(() => {
                            attachCloseButtonByViewport();
                        });
                        updateProductsListCountClass(state);

                        if (state.query !== lastQuery) {
                            showMinMessage = false;
                            lastQuery = state.query;
                        }

                        const hasResults = (state.collections || []).some((collection) => {
                            if (!collection || !collection.source) {
                                return false;
                            }

                            const sourceId = collection.source.sourceId;
                            const isResultSource = sourceId === 'querySuggestionsPlugin' || sourceId === 'products' || sourceId === 'departments';

                            return isResultSource && Array.isArray(collection.items) && collection.items.length > 0;
                        });

                        canSaveRecentSearch = state.query.trim().length >= MIN_QUERY_LENGTH && hasResults;
                    },
                    reshape({ sources, sourcesBySourceId, state }) {
                        const querySuggestionsSource = sourcesBySourceId.querySuggestionsPlugin;
                        const productsSource = sourcesBySourceId.products;
                        const departmentsSource = sourcesBySourceId.departments;
                        const recentSearchesSource = sourcesBySourceId.recentSearchesPlugin;

                        if (!productsSource && !departmentsSource && !recentSearchesSource) {
                            return sources;
                        }

                        const rawSuggestionsItems = querySuggestionsSource ? querySuggestionsSource.getItems() : [];
                        const departmentsItems = departmentsSource ? departmentsSource.getItems() : [];
                        const productsItems = productsSource ? productsSource.getItems() : [];
                        const recentItems = recentSearchesSource ? recentSearchesSource.getItems() : [];
                        const isEmptyQuery = state.query.trim().length < 1;
                        const hasAuxSource = departmentsItems.length > 0 || recentItems.length > 0;
                        const maxSuggestionResults = getMaxSuggestionResults();
                        const suggestionsLimit = hasAuxSource ? LEFT_COLUMN_SUGGESTIONS_WITH_AUX_MAX : maxSuggestionResults;
                        const suggestionsCount = Math.min(rawSuggestionsItems.length, suggestionsLimit);
                        const auxBudget = Math.max(0, LEFT_COLUMN_TOTAL_MAX - suggestionsCount);

                        let departmentsLimit = 0;
                        let recentLimit = 0;

                        if (departmentsItems.length > 0) {
                            departmentsLimit = Math.min(LEFT_COLUMN_AUX_MAX, auxBudget, departmentsItems.length);
                        } else if (recentItems.length > 0) {
                            recentLimit = isEmptyQuery
                                ? Math.min(LEFT_COLUMN_AUX_MAX, recentItems.length)
                                : Math.min(LEFT_COLUMN_AUX_MAX, auxBudget, recentItems.length);
                        }

                        const productsLimit = getMaxItemResults();

                        let reshapedSources = sources.map((source) => {
                            if (source.sourceId === 'querySuggestionsPlugin') {
                                return {
                                    ...source,
                                    getItems() {
                                        return source.getItems().slice(0, suggestionsLimit);
                                    },
                                };
                            }

                            if (source.sourceId === 'products') {
                                return {
                                    ...source,
                                    getItems() {
                                        return source.getItems().slice(0, productsLimit);
                                    },
                                };
                            }

                            if (source.sourceId === 'departments') {
                                return {
                                    ...source,
                                    getItems() {
                                        return source.getItems().slice(0, departmentsLimit);
                                    },
                                };
                            }

                            if (source.sourceId === 'recentSearchesPlugin') {
                                return {
                                    ...source,
                                    getItems() {
                                        return source.getItems().slice(0, recentLimit);
                                    },
                                };
                            }

                            return source;
                        });

                        const hasAlternativeResults = departmentsItems.length > 0 || productsItems.length > 0;
                        const shouldShowMinCharsMessage = showMinMessage && state.query.trim().length < MIN_QUERY_LENGTH;

                        if (state.query.trim().length >= 1 && suggestionsCount < 1 && hasAlternativeResults && !shouldShowMinCharsMessage) {
                            reshapedSources = reshapedSources.filter((source) => source.sourceId !== 'querySuggestionsPlugin');
                        }

                        if (recentLimit > 0) {
                            const recentIndex = reshapedSources.findIndex((source) => source.sourceId === 'recentSearchesPlugin');

                            if (recentIndex > -1) {
                                const recentSource = reshapedSources.splice(recentIndex, 1)[0];
                                reshapedSources.push(recentSource);
                            }
                        }

                        const imageSourceIndex = reshapedSources.findIndex((source) => source.sourceId === 'products');

                        if (imageSourceIndex > -1) {
                            const imageSource = reshapedSources.splice(imageSourceIndex, 1)[0];
                            reshapedSources.push(imageSource);
                        }

                        return reshapedSources;
                    },
                    onSubmit({ state }) {
                        const sanitizedQuery = sanitizeForUrl(state.query);
                        const queryLength = sanitizedQuery.trim().length;
                        
                        if (queryLength >= MIN_QUERY_LENGTH) {
                            window.location.href = `/search/fullsearch/${sanitizedQuery}/`;
                        } else {
                            showMinMessage = queryLength >= 1;
                            const input = document.querySelector('.aa-Input');
                            if (input) {setTimeout(function() {input.focus(), 50}); }
                        }
                    },
                    renderer: { createElement: h, Fragment, render, hydrate() {}, },
                });

                // Assign the initialized instance to the global window object
                window.autocompleteInstance = autocompleteInstance;
            };

            onMounted(() => {
                if (!sessionStore.departments || !Object.keys(sessionStore.departments).length) {
                    sessionStore.getDepartments();
                }

                initializeAutocomplete();
                document.addEventListener('keydown', handleAutocompleteKeydown, true);
                document.addEventListener('focusin', handleAutocompleteFocusIn, true);

                window.requestAnimationFrame(() => {
                    attachCloseButtonByViewport();
                });

                window.addEventListener('resize', () => {
                    window.requestAnimationFrame(() => {
                        attachCloseButtonByViewport();

                        if (!window.matchMedia('(max-width: 768px)').matches) {
                            document.body.classList.remove('search-open-mobile');
                        }
                    });
                });

                // Close autocomplete on scroll
                window.addEventListener('scroll', () => {
                    const isDesktopViewport = window.matchMedia('(min-width: 769px)').matches;

                    if (window.autocompleteInstance && typeof window.autocompleteInstance.setIsOpen === 'function' && isDesktopViewport) {
                        window.autocompleteInstance.setIsOpen(false);
                        const container = document.querySelector('#autocomplete')?.closest('.algolia-mega-menu-container');
                        const panelContainer = document.querySelector('#autocomplete-panel');
                        const autocompleteRoot = document.querySelector('#autocomplete');
                        if (container) {
                            container.classList.remove('is-open');
                        }

                        if (panelContainer) {
                            panelContainer.classList.remove('is-open');
                        }

                        if (autocompleteRoot) {
                            autocompleteRoot.classList.remove('is-open');
                        }
                    }
                });
            });

            onBeforeUnmount(() => {
                document.body.classList.remove('search-open-mobile');
                document.removeEventListener('keydown', handleAutocompleteKeydown, true);
                document.removeEventListener('focusin', handleAutocompleteFocusIn, true);
            });

            return {
                closeAutocompletePanel,
            };
        },
    };
</script>

<style>
    #appMegaMenu .header-search-container
    {
        display: block;
    }

    .algolia-mega-menu-container,
    .algolia-mega-menu
    {
        height: 100%;
        width: 100%;
    }

    #appMegaMenu .searchbox {
        position: relative;
    }

    #appMegaMenu .ais-Hits {
        background-color: #fff;
        border: 1px solid #999;
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
        box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
        left: 0;
        position: relative;
        top: -5px;
        width: calc(100% - 2px);
        z-index: 99999;
    }

    #appMegaMenu .ais-Hits--empty {
        display: none;
    }

    #appMegaMenu .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem;
    }

    #appMegaMenu .search-panel {
        display: flex;
        margin-top: 20px;
    }

    #appMegaMenu .search-panel__results {
        flex: 3;
    }

    #appMegaMenu .pagination {
        margin: 2rem auto;
        text-align: center;
    }

    #appMegaMenu .ais-Hits-item article {
        display: flex;
        padding: 0;
    }

    #appMegaMenu .ais-Hits-item img {
        max-height: 125px;
        padding-right: 16px;
    }

    #appMegaMenu input[type='search'] {
        border: 2px solid #5C8727;
        border-radius: 0;
        color: #333;
        font-size: 14px;
        font-weight: 400;
        height: 48px;
        margin-top: 0;
        padding-left: 44px;
        padding-right: 137px;
        transition: box-shadow 0.3s;
    }

    #appMegaMenu input[type='search']::-webkit-input-placeholder
    {
        color: #333;
    }

    #appMegaMenu ol.ais-Hits-list {
        margin-top: 0;
        list-style-type: none;
        padding: 0;
        margin: 0;
    }

    #appMegaMenu .customResult {
        color: #333;
        display: block;
        font-weight: 700;
        padding: 7px 7px 7px 14px;
        text-decoration: none;
        text-transform: lowercase;
    }

    #appMegaMenu .ais-Highlight-highlighted {
        background-color: transparent;
        color: #757575;
        font-weight: 400;
    }

    #appMegaMenu .ais-Snippet-highlighted {
        background-color: transparent;
        color: #757575;
        font-weight: 400;
    }

    #appMegaMenu a 
    {
        color: #333;
        display: block;
        font-size: 14px;
        font-weight: 700;
        padding: 7px 7px 7px 14px;
        text-decoration: none;
    }
    
    #appMegaMenu a:focus-visible,
    #appMegaMenu button
    {
        box-shadow: none;
    }

    #appMegaMenu img { 
        max-width: 100%;
    }

    .aa-result {
        color: #757575;
        display: block;
        padding: 6px;
    }

    #appMegaMenu .aa-PanelLayout {
        column-gap: 20px;
        row-gap: 10px;
        display: grid;
        grid-template-columns: minmax(20%, 300px) minmax(60%, auto);
        grid-template-rows: auto auto;
        align-items: start;
        width: max-content;
        max-width: 100%;
        min-width: 100%;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="querySuggestionsPlugin"],
    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="recentSearchesPlugin"],
    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="departments"] {
        grid-column: 1;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] {
        grid-column: 2;
        grid-row: 1 / -1;
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 0 6px 6px 0;
        width: 100%;
        min-width: 0;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-SourceFooter {
        display: flex;
        justify-content: flex-start;
        margin-top: auto;
        width: 100%;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-ItemContentTitle .result-header {
        padding-left: 0;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List {
        display: flex;
        gap: 24px;
        padding: 6px 0;
        width: calc(100% + 24px);
    }

    #appMegaMenu .aa-PanelLayout .aa-List .aa-Item * {
        overflow: hidden;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
        box-sizing: border-box;
        flex: 0 0 calc(25% - 24px);
        max-width: 175px;
        min-width: 0;
        overflow: hidden;
        width: 25%;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List.item-count-1 .aa-Item {
        flex-basis: 175px;
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List.item-count-2 .aa-Item {
        flex-basis: calc(50% - 24px);
    }

    #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List.item-count-3 .aa-Item {
        flex-basis: calc(100% / 3 - 24px);
    }

    #appMegaMenu .aa-ItemWrapper--imageResult {
        display: block;
        padding: 0;
        width: 100%;
    }

    #appMegaMenu a.result-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        overflow: hidden;
        padding: 0;
        text-decoration: none;
        width: 100%;
    }

    #appMegaMenu a.result-item:hover {
        text-decoration: none;
    }

    #appMegaMenu .result-item__image {
        align-items: center;
        border: 1px solid #b7b7b7;
        border-radius: 6px;
        display: block;
        max-width: 100%;
        overflow: hidden;
        padding-top: calc(100% - 4px);
        position: relative;
        width: 100%;
        transition: border-color 0.3s;
    }

    #appMegaMenu .result-item__image img {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }

    #appMegaMenu .result-item__image img.product-eb
    {
        display: block;
        height: auto;
        left: 0;
        max-width: 40%;
        position: absolute;
        top: 0;
        width: 92px;
    }

    #appMegaMenu .result-item__image.is-sale
    {
        border: 1px solid transparent;
        position: relative;

        background: linear-gradient(90deg, #b7b7b7 55%, transparent 45%),
                    linear-gradient(90deg, #b7b7b7 55%, transparent 45%),
                    linear-gradient(0deg, #b7b7b7 55%, transparent 45%),
                    linear-gradient(0deg, #b7b7b7 55%, transparent 45%);
        background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
        background-size: 15px 1px, 15px 1px, 1px 15px, 1px 15px;
        background-position: 0px -1px, 100% calc(100% + 1px), -1px 100%, calc(100% + 1px) 0px;
    }

    #appMegaMenu a.result-item:hover .result-item__image,
    #appMegaMenu a.result-item:focus-visible .result-item__image {
        border-color: #333;
    }

    #appMegaMenu .result-item__brand,
    #appMegaMenu .result-item__text,
    #appMegaMenu .result-item__info {
        color: #333;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.4em;
        padding: 0;
    }

    #appMegaMenu .result-item__text {
        font-weight: 600;
    }

    #appMegaMenu .result-item__text mark {
        background: transparent;
        color: #757575;
        font-weight: 400;
    }

    #appMegaMenu .result-item__brand {
        color: #757575;
        font-size: 9px;
        overflow: hidden;
        text-overflow: ellipsis;
        text-transform: uppercase;
        white-space: nowrap;
    }

    #appMegaMenu .result-item__info {
        align-self: flex-start;
        display: grid;
        font-size: 14px;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        grid-column-gap: 8px;
        grid-row-gap: 4px;
    }

    #appMegaMenu .result-item__info-price {
        font-weight: 600;
    }

    #appMegaMenu .result-item__info-price.is-sale {
        align-items: center;
        background-color: #ffcd00;
        display: flex;
        font-weight: 600;
        height: 20px;
        padding: 2px;
        text-align: center;
    }

    #appMegaMenu .result-item__info-size {
        text-transform: lowercase;
    }

    #appMegaMenu .aa-result--image-column {
        padding-left: 16px;
    }

    #appMegaMenu .aa-SeeAllWrapper--images {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
    }

    #appMegaMenu .aa-SeeAllWrapper--images a.see-all,
    #appMegaMenu .aa-SeeAllWrapper--images a.see-all:hover,
    #appMegaMenu .aa-SeeAllWrapper--images a.see-all:focus-visible {
        background: #5C8727;
        color: #fff;
        font-weight: 600;
        line-height: 20px;
        min-width: 230px;
        padding: 5px 20px;
        text-align: center;
        text-decoration: none;
        transition: all 0.3s;;
    }

    #appMegaMenu a .ais-Highlight-highlighted
    {
        color: #757575;
        font-weight: 400;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-trending
    {
        color: #333;
        display: block;
        font-weight: 600;
        line-height: 28px;
        overflow: hidden;
        padding: 6px 6px 6px 38px;
        position: relative;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-trending:hover {
        text-decoration: underline;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-trending:focus-visible {
        box-shadow: inset 1px 1px 0 #b7b7b7, inset -1px -1px 0 #b7b7b7;
        text-decoration: underline;
    }

    #appMegaMenu .aa-SourceFooter a,
    #appMegaMenu .aa-SourceFooter a:hover,
    #appMegaMenu .aa-SourceFooter a:focus-visible {
        color: #757575;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-trending .result-svg
    {
        height: 16px;
        left: 8px;
        position: absolute;
        top: 10px;
        width: 16px;;
    }

    #appMegaMenu .aa-Source[data-autocomplete-source-id="recentSearchesPlugin"] .aa-List {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 6px;
    }

    #appMegaMenu .aa-Source[data-autocomplete-source-id="recentSearchesPlugin"] .aa-Item {
        width: auto;
    }

    #appMegaMenu .aa-Source[data-autocomplete-source-id="departments"] .aa-List {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding: 6px;
    }

    #appMegaMenu .aa-Source[data-autocomplete-source-id="departments"] .aa-Item {
        width: auto;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-department,
    #appMegaMenu .aa-ItemContentTitle a.result-recent {
        background: #eee;
        border: 1px solid #ccc;
        border-radius: 999px;
        color: #333;
        display: inline-block;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.2;
        max-width: 100%;
        overflow: hidden;
        padding: 6px 12px;
        position: relative;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: auto;
        transition: all 0.3s;
    }

    #appMegaMenu .aa-ItemContentTitle a.result-department:hover,
    #appMegaMenu .aa-ItemContentTitle a.result-recent:hover,
    #appMegaMenu .aa-ItemContentTitle a.result-department:focus-visible,
    #appMegaMenu .aa-ItemContentTitle a.result-recent:focus-visible {
        background-color: #333;
        color: #fff;
        text-decoration: none;
    }

    #appMegaMenu #search-clear,
    #appMegaMenu input[type="search" i]::-webkit-search-cancel-button 
    {
        display: none;
    }

    .algolia-search-form.department-search .department-search-container input[type="text"]{
        font-size: 20px;
        height: 60px;
        margin-top: 0;
        padding-left: 15px;
        padding-right: 65px;
        transition: box-shadow 0.3s;
    }

    #autocomplete {
        margin-top: 25px;
    }

    #autocomplete-panel
    {
        position: relative;
    }

    .aa-Panel {
        padding: 18px 10px;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: max-content !important;
        max-width: calc(100vw - 600px) !important;
        min-width: calc(100% - 20px);
        z-index: 1000000;
    }

    .aa-Form {
        align-items: center;
        display: flex;
        margin: 0;
        position: relative;
        width: 100%;
    }

    .aa-Form {
        border: none;
        position: relative;
        z-index: 1;
    }

    .aa-InputWrapper {
        order: 3;
        position: relative;
        width: 100%;
        z-index: -1;
    }

    .aa-InputWrapper::after{
        content: '';
        background: url(/images/svg/search.svg) no-repeat center center;
        height: 24px;
        left: 24px;
        top: 24px;
        position: absolute;
        width: 24px;

        filter: brightness(0) saturate(100%) invert(12%) sepia(0%) saturate(2757%) hue-rotate(253deg) brightness(105%) contrast(80%);
        transform: scale(0.8) translate(calc(-50% - 4px), calc(-50% - 4px));
        transition: all 0.3s, height 0s, width 0s;
    }

    .aa-InputWrapperSuffix {
        align-items: center;
        display: flex;
        height: 44px;
        left: 0;
        position: absolute;
        z-index: 9999;
        order: 4;
    }

    .aa-Input {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background: none;
        border: 0;
        font: inherit;
        height: 44px;
        padding: 0;
        width: 100%;
    }
    
    .aa-Input::-moz-placeholder {
        opacity: 1;
    }

    .aa-Input::placeholder {
        opacity: 1;
    }

    .aa-Input:focus {
        border-color: none;
        box-shadow: none;
        outline: none;
    }

    .aa-Input::-webkit-search-cancel-button,
    .aa-Input::-webkit-search-decoration,
    .aa-Input::-webkit-search-results-button,
    .aa-Input::-webkit-search-results-decoration {
        -webkit-appearance: none;
        appearance: none;
    }

    .aa-ClearButton {
        align-items: center;
        background: none;
        border: 0;
        cursor: pointer;
        display: flex;
        height: 100%;
        margin: 0;
    }

    #appMegaMenu .aa-Form button
    {
        border: 1px solid transparent;
        cursor: pointer;
        height: 60px;
        overflow: hidden;
        padding: 1px 6px;
        position: absolute;
        top: 0;
        width: 60px;
        transition: background-color 0.3s, background-size 0.3s, border-color 0.3s;
    }

    #appMegaMenu .aa-Form button.aa-SubmitButton {
        background-color: #5C8727;
        border-radius: 0;
        font-family: oswald, sans-serif;
        font-size: 18px;
        font-weight: 600;
        height: 34px;
        letter-spacing: 1px;
        margin: 7px;
        width: 126px;
    }

    #appMegaMenu .aa-Form button.aa-SubmitButton:hover,
    #appMegaMenu .aa-Form button.aa-SubmitButton:focus-visible,
    #appMegaMenu .aa-SeeAllWrapper--images a.see-all:hover,
    #appMegaMenu .aa-SeeAllWrapper--images a.see-all:focus-visible {
        background-color: #333;
    }

    #appMegaMenu .aa-Form button svg
    {
        fill: #333;
        height: 30px;
        max-height: 100%;
        width: 30px;
        transition: all 0.3s, height 0s, width 0s;
        transform: scale(0.8);
    }

    #appMegaMenu .aa-Form button:hover svg,
    #appMegaMenu .aa-Form button:focus-visible svg {
        transform: scale(1);
    }

    #appMegaMenu .aa-Form button.aa-SubmitButton svg {
        display: none;
    }

    #appMegaMenu .aa-Form button.aa-ClearButton 
    {
        background-color: #fff;
        border: none;
        border-radius: 5px;
        height: 30px;
        left: 7px;
        padding: 0;
        top: 6px;
        width: 30px;
    }

    #appMegaMenu .aa-SourceNoResults {
        padding: 0;
    }

    .aa-LoadingIndicator {
        display: none;
    }

    .aa-DetachedCancelButton {
        color: #fff;
        display: none;
        height: 24px;
        position: absolute;
        overflow: hidden;
        right: 15px;
        text-indent: 100px;
        top: 15px;
        width: 24px;
        z-index: 9999999;
    }

    #appMegaMenu button.aa-DetachedCancelButton:focus-visible {
        box-shadow: inset 0 1px 1px rgba(0,0,0,0.075), 0 0 6px rgba(91,135,38,0.7);
    }

    .aa-DetachedCancelButton:before,
    .aa-DetachedCancelButton:after
    {
        background-color: #757575;
        border-radius: 5px;
        content: '';
        height: 3px;
        left: 0;
        position: absolute;
        top: 10px;
        width: 100%;

        -webkit-transition: background-color 0.3s;
            -o-transition: background-color 0.3s;
                transition: background-color 0.3s;
    }

    .aa-DetachedCancelButton:before
    {
        -webkit-transform: rotate(45deg);
            -ms-transform: rotate(45deg);
                transform: rotate(45deg);
    }

    .aa-DetachedCancelButton:after
    {
        -webkit-transform: rotate(315deg);
            -ms-transform: rotate(315deg);
                transform: rotate(315deg);
    }

    .aa-DetachedCancelButton:hover:before,
    .aa-DetachedCancelButton:focus-visible:before,
    .aa-DetachedCancelButton:hover:after,
    .aa-DetachedCancelButton:focus-visible:after {
        background-color: #333;
        box-shadow: none !important;
    }

    @media only screen and (min-width: 769px)
    {
        #autocomplete-panel.is-open .aa-Panel .aa-DetachedCancelButton {
            display: block;
        }
    }

    @media only screen and (max-width: 1400px)
    {
        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
            flex: 0 0 calc(100% / 3 - 24px);
            width: calc(100% / 3);
        }
    }

    @media only screen and (max-width: 1200px)
    {
        #appMegaMenu .aa-PanelLayout {
            display: flex;
            flex-direction: column
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="departments"],
        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="recentSearchesPlugin"],
        #appMegaMenu .result-item__brand,
        #appMegaMenu .result-item__info {
            display: none;
        }

        #appMegaMenu .aa-SeeAllWrapper--images {
            justify-content: center;
        }
    }

    @media only screen and (max-width:1024px)
    {
        #appMegaMenu input[type='search'] {
            padding-right: 90px;
        }

        #appMegaMenu .aa-Form button.aa-SubmitButton {
            width: 80px;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] {
            padding-left: 6px;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List {
            gap: 12px;
            width: calc(100% + 12px);
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
            flex-basis: calc(100% / 3 - 12px);
        }
    }

    @media only screen and (max-width: 992px)
    {
        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
            flex-basis: calc(50% - 12px);
        }
    }

    @media only screen and (max-width: 768px)
    {
        body.search-open-mobile {
            height: 100vh;
            overflow: hidden;
            touch-action: none;
        }

        .aa-DetachedCancelButton {
            top: 35px;
        }

        #autocomplete.is-open .aa-DetachedCancelButton {
            display: block;
        }

        .aa-DetachedCancelButton:before,
        .aa-DetachedCancelButton:after
        {
            background-color: #333;
        }

        .keywordContainer #appMegaMenu {
            margin: 0;
        }

        .aa-Panel {
            display: flex;
            -webkit-box-orient: vertical;
            -webkit-box-direction: normal;
                -ms-flex-direction: column;
                    flex-direction: column;
            margin-top: 5px;
            max-width: 100% !important;
            max-height: calc(100vh - 90px);
            max-height: calc(100dvh - 90px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding: 0;
        }

        .is-open .aa-Panel {
            box-shadow: none;
        }

        .is-open .algolia-mega-menu
        {
            margin: 0 10px;
        }

        .aa-Panel--scrollable {
            -webkit-box-flex: 1;
                -ms-flex: 1 1 auto;
                    flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
        }

        #autocomplete {
            margin-top: 20px;
        }

        .is-open .aa-Autocomplete {
            width: calc(100% - 50px);
        }

        .aa-Form{
            height: 48px;
        }

        .aa-InputWrapperSuffix {
            left: unset;
            right: 50px;
        }

        #appMegaMenu input[type='search'] {
            background-color: #eee;
            border: none;
        }

        .aa-InputWrapper::after,
        #appMegaMenu .aa-Form button.aa-SubmitButton::after {
            display: none;
        }

        #appMegaMenu .aa-Form button.aa-SubmitButton {
            content: '';
            background: url(/images/svg/search.svg) no-repeat center center;
            height: 24px;
            left: 6px;
            top: 4px;
            padding: 0;
            position: absolute;
            width: 24px;

            filter: brightness(0) saturate(100%) invert(12%) sepia(0%) saturate(2757%) hue-rotate(253deg) brightness(105%) contrast(80%);
        }

        #appMegaMenu .aa-Form button.aa-ClearButton 
        {
            background-color: #eee;
            height: 24px;
            top: 10px;
            width: 24px;
        }

        #appMegaMenu .aa-PanelLayout {
            display: block;
        }

        .aa-ItemContentTitle .result-header {
            padding-left: 0;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] {
            border-left: 0;
            grid-row: auto;
            height: auto;
            padding: 0;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List {
            /* flex-wrap: wrap; */
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-template-rows: repeat(2, 1fr);
            grid-column-gap: 12px;
            grid-row-gap: 12px;
            width: 100%;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
            /* flex-basis: calc(25% - 12px); */
            width: 100%;
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-SourceFooter {
            justify-content: center;
            margin-top: 0;
        }

        #appMegaMenu .aa-SeeAllWrapper--images {
            margin-bottom: 20px;
        }
    }

    @media only screen and (max-width: 500px)
    {
         #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(2, 1fr);
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item {
            flex-basis: calc(100% / 3 - 12px);
        }

        #appMegaMenu .aa-PanelLayout [data-autocomplete-source-id="products"] .aa-List .aa-Item:nth-child(n+7) {
            display: none;
        }
    }
</style>